import * as React from "react";

import {
    ColumnBuilder,
    ColumnOptions,
    DEFAULT_COLUMN_BUILDER_ID,
} from "@impero-com/common/components/dataTable";
import { t } from "@impero-com/common/utils/i18n";
import { InfoTooltip } from "@impero-com/ui";
import { uniq } from "lodash-es";

import { EntityView } from "api/generated/model";
import { ASSIGNEE_CATEGORIES_ID } from "utils/assigneeTags";
import { Guid } from "utils/guid";

import {
    ResponsibleCell,
    FirstReviewerCell,
    SecondReviewerCell,
    TagsCell,
    EntityCell,
} from "./cellWidgets";
import * as theme from "./theme.css";
import { AssignmentRow } from "./types";

function buildColumns(entitiesById: Map<number, EntityView>): {
    [extractorId: string]: ColumnBuilder<AssignmentRow, any>;
} {
    return {
        responsible: (_extractor): ColumnOptions<AssignmentRow> => ({
            getFormattedValue: (assignmentRow) => (
                <ResponsibleCell {...{ assignmentRow }} />
            ),
        }),
        firstReviewer: (_extractor): ColumnOptions<AssignmentRow> => ({
            getFormattedValue: (assignmentRow) => (
                <FirstReviewerCell {...{ assignmentRow }} />
            ),
        }),
        secondReviewer: (_extractor): ColumnOptions<AssignmentRow> => ({
            getFormattedValue: (assignmentRow) => (
                <SecondReviewerCell {...{ assignmentRow }} />
            ),
        }),
        entity: (_extractor): ColumnOptions<AssignmentRow> => ({
            getFormattedValue: (assignmentRow) => (
                <EntityCell
                    {...{ assignmentRow }}
                    entitiesById={entitiesById}
                />
            ),
        }),
        tags: (_extractor): ColumnOptions<AssignmentRow> => ({
            getFormattedValue: (assignmentRow) => (
                <TagsCell
                    tagIds={mergeEntityTagIds(assignmentRow, entitiesById)}
                />
            ),
            sortable: false,
        }),
        [DEFAULT_COLUMN_BUILDER_ID]: (extractor) => {
            if (
                extractor.id.startsWith("onlySearch") ||
                extractor.id.startsWith(ASSIGNEE_CATEGORIES_ID)
            ) {
                return {
                    display: "NEVER",
                };
            }
            return {
                display: "DEFAULT_VISIBLE",
            };
        },
    };
}

function mergeEntityTagIds(
    assignmentRow: AssignmentRow,
    entitiesById: Map<number, EntityView>
): Guid[] {
    const { entityId } = assignmentRow;

    const entityTagIds =
        entityId !== undefined
            ? (entitiesById.get(entityId)?.tagIds ?? [])
            : [];

    return uniq([...assignmentRow.tagIds, ...entityTagIds]);
}

class TableSectionHeader extends React.PureComponent {
    render = () => (
        <div className={theme.labelWithTooltipContainer}>
            <h4 className={theme.labelWithTooltip}>
                {t("Control_Assignments")}
            </h4>
            <InfoTooltip text={t("Control_Assignments_Tooltip")} size="small" />
        </div>
    );
}

export { TableSectionHeader, buildColumns };
